import { collection, getDocs } from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';

export interface City {
  id: string;
  name: string;
  keys: string[]; // Keep for backward compatibility
  coordinates?: {
    latitude: number;
    longitude: number;
  };
  radiusKm?: number; // Radius in kilometers for geofencing
}

class CityService {
  private readonly COLLECTION_NAME = 'cities';
  private cachedCities: City[] | null = null;

  /**
   * Calculate distance between two coordinates using Haversine formula
   * Returns distance in kilometers
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRadians(lat2 - lat1);
    const dLon = this.toRadians(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(lat1)) *
        Math.cos(this.toRadians(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Check if coordinates are within a city's geofence
   */
  private isWithinCity(
    latitude: number,
    longitude: number,
    city: City
  ): boolean {
    // Skip cities without coordinates
    if (!city.coordinates || !city.radiusKm) {
      return false;
    }

    const distance = this.calculateDistance(
      latitude,
      longitude,
      city.coordinates.latitude,
      city.coordinates.longitude
    );
    return distance <= city.radiusKm;
  }

  async fetchCities(): Promise<City[]> {
    // Return cached cities if available
    if (this.cachedCities) {
      return this.cachedCities;
    }

    try {
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      const cities = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as City[];

      this.cachedCities = cities;
      return cities;
    } catch (error) {
      console.error('Error fetching cities:', error);
      throw new Error('Failed to fetch cities');
    }
  }

  /**
   * Finds a city by checking if coordinates are within its geofence
   */
  async findCityByCoordinates(
    latitude: number,
    longitude: number
  ): Promise<City | null> {
    if (!this.cachedCities) {
      await this.fetchCities();
    }

    // Find the first city that contains these coordinates
    return (
      this.cachedCities?.find(city =>
        this.isWithinCity(latitude, longitude, city)
      ) || null
    );
  }

  /**
   * Finds a city that matches the given location based on city keys
   * @deprecated Use findCityByCoordinates for better accuracy
   */
  async findCityByLocation(city: string): Promise<City | null> {
    const searchText = `${city}`.toLowerCase();

    if (!this.cachedCities) {
      await this.fetchCities();
    }

    return this.cachedCities?.find(city => city.keys.includes(searchText)) || null;
  }

  /**
   * Clears the cache (useful for testing or when cities are updated)
   */
  clearCache(): void {
    this.cachedCities = null;
  }
}

export const cityService = new CityService();
